import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { PrimeIcons } from 'primereact/api'
import { Button } from 'primereact/button'
import { Column } from 'primereact/column'
import { DataTable } from 'primereact/datatable'
import { Message } from 'primereact/message'
import { Updater } from 'use-immer'

import { ActiveBodyTemplate } from '../../../../../../components/tableTemplates'
import {
  HandleableError,
  getHandleableError,
} from '../../../../../../utils/errors/isXiaError'
import { LoadingScreen } from '../../../../master-data-management/packaging-data/components/dialogs/Upload/StepperPanels/common/LoadingScreen'
import { requestUploadSupplierList } from '../../../data/api'
import { SupplierItemList } from '../../../data/model'
import { SuppliersTableState } from './SupplierUploadDialog'

type Props = {
  goToNextStep: () => void
  goToPrevStep: () => void
  setCsvData: React.Dispatch<React.SetStateAction<SupplierItemList | null>>
  setError: React.Dispatch<React.SetStateAction<HandleableError | null>>
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>
  data: SupplierItemList
  isLoading: boolean
  error: HandleableError | null
  setFooter: (content: React.ReactNode) => void
  updateState: Updater<SuppliersTableState>
}

export const VerifyPanel: React.FC<Props> = ({
  goToNextStep,
  goToPrevStep,
  setCsvData,
  setError,
  setIsLoading,
  data,
  isLoading = false,
  error,
  setFooter,
  updateState,
}) => {
  const { t } = useTranslation()

  // Footer
  useEffect(() => {
    setFooter(
      <div className="flex pt-4 justify-content-between">
        <Button
          label={t('suppliers.table.upload.dialog.verifyPanel.footer.previous')}
          icon={PrimeIcons.ARROW_LEFT}
          onClick={onPrev}
          disabled={isLoading}
        />
        <Button
          label={t('suppliers.table.upload.dialog.verifyPanel.footer.next')}
          icon={PrimeIcons.CHECK}
          onClick={onNext}
          disabled={isLoading || !!error}
        />
      </div>,
    )
  }, [isLoading, error])

  const uploadFile = async () => {
    try {
      setIsLoading(true)

      const response = await requestUploadSupplierList(data)

      triggerStatusRequest(
        true,
        false,
        true,
        response.locationUrl,
        response.retry,
      )
    } catch (error) {
      const handleableError = getHandleableError(error)
      setError(handleableError)
    } finally {
      setIsLoading(false)
    }
  }

  const triggerStatusRequest = (
    isCreateValue: boolean,
    isDeleteValue: boolean,
    isSupplier: boolean,
    location: string,
    retryAfter: string,
  ) => {
    updateState(draft => {
      draft.statusDialog.isVisible = true
      draft.statusDialog.isRequestRunning = true
      draft.statusDialog.isCreate = isCreateValue
      draft.statusDialog.isDelete = isDeleteValue
      draft.statusDialog.location = location
      draft.statusDialog.retryAfter = retryAfter
      draft.statusDialog.isSupplierProcess = isSupplier

      return draft
    })
  }

  const onNext = () => {
    uploadFile()
    goToNextStep()
  }

  const onPrev = () => {
    setError(null)
    setCsvData(null)
    goToPrevStep()
  }

  if (isLoading) return <LoadingScreen />

  if (error) {
    const content = (
      <div className="flex align-items-center">
        <i
          className="pi pi-times-circle text-red-500 mr-2"
          style={{ fontSize: '1.25rem' }}
        ></i>

        <div className="ml-2">
          <h4 className="m-0">
            {t('suppliers.table.upload.dialog.verifyPanel.error')}:{' '}
            {error.title}
          </h4>
          <div className="mt-1">{error.message}</div>
        </div>
      </div>
    )

    return (
      <Message
        severity="error"
        className="w-full justify-content-start"
        content={content}
      />
    )
  }

  return (
    <>
      <DataTable
        value={data}
        paginator
        rowsPerPageOptions={[10, 20, 50]}
        rows={10}
        scrollable
        className="mt-4"
      >
        <Column
          field="name"
          header={t(
            'suppliers.table.upload.dialog.verifyPanel.table.field.columnName.name',
          )}
          sortable
          filter
        />
        <Column
          field="displayName"
          header={t(
            'suppliers.table.upload.dialog.verifyPanel.table.field.columnName.displayName',
          )}
          sortable
          filter
        />
        <Column
          field="description"
          header={t(
            'suppliers.table.upload.dialog.verifyPanel.table.field.columnName.description',
          )}
          sortable
          filter
        />
        <Column
          field="templateName"
          header={t(
            'suppliers.table.upload.dialog.verifyPanel.table.field.columnName.templateName',
          )}
          sortable
          filter
        />
        <Column
          field="mode"
          header={t(
            'suppliers.table.upload.dialog.verifyPanel.table.field.columnName.status',
          )}
          body={(rowData: any) =>
            rowData.mode === 'develop'
              ? ActiveBodyTemplate('inactive')
              : ActiveBodyTemplate('active')
          }
          sortable
          filter
        />
      </DataTable>
    </>
  )
}
